/* Shopify path: assets/pro-media-content-video.js */
(() => {
  const MODULE_KEY = '__proMediaContentVideoModuleV1';
  const CORE_KEY = '__proVideoLifecycleCoreV1';
  const QUEUE_KEY = '__proVideoLifecycleModuleQueueV1';

  if (window[MODULE_KEY]) return;
  window[MODULE_KEY] = true;

  const register = (core) => {
    core.registerMode(
      'media-content-premium',
      ({ root, videos, helpers, playback, preload, reducedMotionQuery }) => {
        const video = videos[0];
        if (!video) return null;

        const visibilityTarget =
          root.querySelector('.pro-media-content__media') || root;
        const threshold = helpers.numberFromData(root.dataset.proVideoThreshold, 0.12);
        const mobileViewport = window.matchMedia('(max-width: 989px)').matches;
        const preloadMargin = mobileViewport
          ? root.dataset.proVideoPreloadMarginMobile || '2000px 0px'
          : root.dataset.proVideoPreloadMarginDesktop || '2000px 0px';
        const initialProfile = helpers.connectionProfile();
        const bufferMargin = mobileViewport
          ? (initialProfile === 'balanced' ? '1800px 0px' : '1600px 0px')
          : (initialProfile === 'balanced' ? '1800px 0px' : '1600px 0px');
        const eager = root.dataset.proVideoEager === 'true';
        const abortController = new AbortController();
        const signal = abortController.signal;

        let preloadObserver = null;
        let bufferObserver = null;
        let playObserver = null;
        let fallbackFrame = null;
        let playFrame = null;
        let visible = false;
        let bufferReady = false;
        let tryingToPlay = false;
        let destroyed = false;
        let controller = null;
        let startupTimer = null;
        let startupSince = 0;
        let safariPriming = false;
        const iosWebKit = /iP(hone|ad|od)/i.test(navigator.userAgent) ||
          (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

        helpers.prepareVideo(video, {
          autoplayAttribute: false,
          preload: video.preload || video.getAttribute('preload') || 'metadata',
          loopMode: 'runtime',
        });

        const setPosterVisible = (showPoster) => {
          root.classList.toggle('is-video-playing', !showPoster);
        };

        const currentPolicy = () => helpers.resourcePolicy();

        const isEligible = () => {
          const policy = currentPolicy();
          return (
            !destroyed &&
            visible &&
            !document.hidden &&
            !reducedMotionQuery.matches &&
            policy.allowAutoplay
          );
        };

        const warmNearViewport = () => {
          if (destroyed) return;
          const policy = currentPolicy();

          // Re-evaluate real buffer coverage when the section approaches.
          // A completed full warm may need to be granted again if the browser
          // has since evicted its offscreen media buffer.
          preload.refresh();

          if (!policy.allowAutoplay || reducedMotionQuery.matches) {
            helpers.setPreload(video, policy.farPreload ?? 'none', { load: false });
            return;
          }

          // Never downgrade an already fully warmed or actively loading video
          // just because the 2000px observer fired after the queue grant.
          if (visible || video.preload === 'auto' || (controller && preload.owns(controller))) {
            return;
          }

          const intent = policy.farPreload ?? 'metadata';
          helpers.setPreload(video, intent, { load: intent !== 'none' });
        };

        const syncSpeculativePreload = () => {
          if (!controller || destroyed) return;

          preload.register(controller, {
            root,
            video,
            warmMode: 'full',
            isUrgent: () => visible || bufferReady,
            isEligible: () => {
              const policy = currentPolicy();
              return (
                !destroyed &&
                !document.hidden &&
                !reducedMotionQuery.matches &&
                policy.allowAutoplay &&
                policy.speculativePreload !== 'none'
              );
            },
            onGrant: () => {
              const policy = currentPolicy();
              helpers.setPreload(video, policy.speculativePreload, {
                load: policy.speculativePreload !== 'none',
              });
            },
            onRevoke: () => {
              if (destroyed || visible) return;
              const policy = currentPolicy();

              // Keep the completed six-second-style loop in the browser's
              // auto-preload state. Demoting it to metadata immediately after
              // full buffering makes it easier for Chrome to discard the
              // offscreen buffer before the shopper reaches this section.
              // A partial warm still yields to the next section as before.
              if (
                policy.allowAutoplay &&
                !reducedMotionQuery.matches &&
                helpers.fullWarmReady(video)
              ) {
                return;
              }

              const intent = policy.farPreload ?? 'metadata';
              helpers.setPreload(video, intent, {
                load: intent !== 'none',
              });
            },
          });
        };

        const prepareVisibleVideo = () => {
          if (destroyed) return;
          preload.refresh();
          const policy = currentPolicy();
          helpers.setPreload(video, policy.visiblePreload, { load: true });
        };

        const clearStartupTimer = () => {
          if (startupTimer !== null) window.clearTimeout(startupTimer);
          startupTimer = null;
        };

        const clearPlayFrame = () => {
          if (playFrame === null) return;
          window.cancelAnimationFrame(playFrame);
          playFrame = null;
        };

        const pause = ({ release = true, showPoster = false, reset = false } = {}) => {
          clearPlayFrame();
          clearStartupTimer();
          startupSince = 0;
          safariPriming = false;
          helpers.pauseVideo(video);
          if (reset && video.readyState > 0) {
            try { video.currentTime = 0; } catch (error) {}
          }
          if (showPoster) setPosterVisible(true);
          if (release && controller) playback.release(controller);
        };

        const attemptAutoplay = () => {
          playFrame = null;
          if (!isEligible() || tryingToPlay || !controller) return;

          // Autoplay is only visually instant if the first loop is already warm.
          // When it isn't, retain the sharp poster while the queue gives this
          // visible video the highest priority. Never expose a 0.5s-buffer stall.
          prepareVisibleVideo();
          const ready = helpers.autoplayStartupReady(video, { full: true, foreground: true });
          if (!ready) {
            if (!startupSince) startupSince = performance.now();
            setPosterVisible(true);

            // iOS WebKit can postpone media bytes until play(). If the visible video
            // still has almost no forward buffer after a short grace period,
            // prime playback behind the poster. Once startup readiness is met,
            // restart at frame zero and reveal only after a painted frame.
            const ahead = helpers.bufferedAhead(video);
            if (iosWebKit && !safariPriming &&
                performance.now() - startupSince > 1100 &&
                (ahead === null || ahead < 0.65)) {
              const claimed = playback.claim(controller, {
                priority: playback.priorities.AUTOPLAY,
                root,
                video,
                pause: (options) => pause({ release: options?.release !== false }),
              });
              if (claimed) {
                safariPriming = true;
                helpers.safePlay(video).then((didPlay) => {
                  if (!didPlay) safariPriming = false;
                });
              }
            }
            if (startupTimer === null) {
              startupTimer = window.setTimeout(() => {
                startupTimer = null;
                scheduleAutoplayCheck();
              }, 220);
            }
            return;
          }

          clearStartupTimer();
          startupSince = 0;
          const claimed = playback.claim(controller, {
            priority: playback.priorities.AUTOPLAY,
            root,
            video,
            pause: (options) => pause({ release: options?.release !== false }),
          });
          if (!claimed) return;

          if (safariPriming && !video.paused && video.currentTime > 0.12) {
            try { video.currentTime = 0; } catch (error) {}
          }
          safariPriming = false;
          tryingToPlay = true;
          helpers.safePlay(video)
            .then((didPlay) => {
              if (!didPlay) playback.release(controller);
              else helpers.revealAfterFirstFrame(video, () => {
                if (isEligible() && helpers.autoplayStartupReady(video, { full: true, foreground: true })) {
                  setPosterVisible(false);
                }
              }, { signal, timeoutMs: 1800 });
            })
            .finally(() => {
              tryingToPlay = false;
              if (!isEligible()) playback.release(controller);
            });
        };

        const scheduleAutoplayCheck = () => {
          if (playFrame !== null || !isEligible() || tryingToPlay) return;
          playFrame = window.requestAnimationFrame(attemptAutoplay);
        };

        const updateFallbackVisibility = () => {
          fallbackFrame = null;
          const nextVisible = helpers.isInViewport(visibilityTarget);

          if (nextVisible) {
            visible = true;
            scheduleAutoplayCheck();
          } else if (visible) {
            visible = false;
            pause({ showPoster: true, reset: true });
          }
        };

        const scheduleFallbackUpdate = () => {
          if (fallbackFrame !== null) return;
          fallbackFrame = window.requestAnimationFrame(updateFallbackVisibility);
        };

        controller = {
          pause,
          onVisibilityChange() {
            preload.refresh();
            if (document.hidden) pause();
            else if (visible) scheduleAutoplayCheck();
          },
          onMotionChange() {
            preload.refresh();
            if (reducedMotionQuery.matches) pause({ showPoster: true });
            else if (visible) scheduleAutoplayCheck();
          },
          onUserGesture() {
            if (visible) scheduleAutoplayCheck();
          },
          destroy() {
            if (destroyed) return;
            destroyed = true;
            clearPlayFrame();
            clearStartupTimer();
            preloadObserver?.disconnect();
            bufferObserver?.disconnect();
            playObserver?.disconnect();
            abortController.abort();
            if (fallbackFrame !== null) window.cancelAnimationFrame(fallbackFrame);
            helpers.pauseVideo(video);
            playback.release(controller);
            preload.unregister(controller);
          },
        };

        video.addEventListener(
          'playing',
          () => {
            if (destroyed) return;
            if (!helpers.autoplayStartupReady(video, { full: true, foreground: true })) return;
            helpers.revealAfterFirstFrame(video, () => {
              if (isEligible() && helpers.autoplayStartupReady(video, { full: true, foreground: true })) {
                setPosterVisible(false);
              }
            }, { signal, timeoutMs: 1800 });
          },
          { signal }
        );

        video.addEventListener(
          'pause',
          () => {
            playback.release(controller);
          },
          { signal }
        );

        video.addEventListener(
          'error',
          () => {
            pause({ showPoster: true });
          },
          { signal }
        );

        ['waiting', 'stalled'].forEach((eventName) => {
          video.addEventListener(
            eventName,
            () => {
              helpers.reportStall(video);
            },
            { passive: true, signal }
          );
        });

        ['loadeddata', 'canplay', 'canplaythrough', 'progress', 'seeked'].forEach((eventName) => {
          video.addEventListener(eventName, scheduleAutoplayCheck, {
            passive: true,
            signal,
          });
        });


        video.addEventListener(
          'ended',
          () => {
            if (!isEligible()) {
              playback.release(controller);
              return;
            }

            try {
              video.currentTime = 0;
            } catch (error) {}
            scheduleAutoplayCheck();
          },
          { signal }
        );


        if ('IntersectionObserver' in window) {
          preloadObserver = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              warmNearViewport();
              preloadObserver?.disconnect();
              preloadObserver = null;
            },
            { root: null, rootMargin: preloadMargin, threshold: 0 }
          );

          bufferObserver = new IntersectionObserver(
            (entries) => {
              bufferReady = entries.some((entry) => entry.isIntersecting);
              preload.refresh();
            },
            { root: null, rootMargin: bufferMargin, threshold: 0 }
          );

          playObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const nextVisible = Boolean(
                  entry.isIntersecting && entry.intersectionRatio >= threshold
                );

                if (nextVisible) {
                  visible = true;
                  preload.refresh();
                  scheduleAutoplayCheck();
                } else if (visible) {
                  visible = false;
                  preload.refresh();
                  pause({ showPoster: true, reset: true });
                }
              });
            },
            {
              root: null,
              rootMargin: '0px',
              threshold: [0, 0.15, threshold, 0.5, 0.75, 1],
            }
          );

          preloadObserver.observe(visibilityTarget);
          bufferObserver.observe(visibilityTarget);
          playObserver.observe(visibilityTarget);
        } else {
          ['scroll', 'resize', 'orientationchange'].forEach((eventName) => {
            window.addEventListener(eventName, scheduleFallbackUpdate, {
              passive: true,
              signal,
            });
          });
          updateFallbackVisibility();
        }

        syncSpeculativePreload();
        if (eager) warmNearViewport();
        if (reducedMotionQuery.matches) setPosterVisible(true);

        return controller;
      }
    );
  };

  if (window[CORE_KEY]) register(window[CORE_KEY]);
  else (window[QUEUE_KEY] ||= []).push(register);
})();
